/**
 * Skeleton data for Critical Illness coverage
 *
 * Returns an empty Critical Illness coverage structure with all required fields
 * set to their default "not included" or empty values.
 *
 * IMPORTANT: Benefits are in GRID FORMAT (flat fields), not API format (nested objects).
 * This allows proper merging with data transformed from API via transformFromApi.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  BenefitPlanTypeEnum,
  CoverageTypeEnum,
  FundingTypeEnum,
  PlanOptionNameEnum,
  RfpBenefitPlan,
  RfpCoverage,
} from "../../types/rfp.api.types";

/**
 * Gets the skeleton structure for a Critical Illness coverage
 *
 * Returns a minimal coverage with empty/default values for all benefit fields in grid format.
 * This is used when creating a new Critical Illness coverage from scratch.
 *
 * @returns Partial RfpCoverage with Critical Illness skeleton data
 */
export function getCriticalIllnessSkeleton(): Partial<RfpCoverage> {
  return {
    type: CoverageTypeEnum.CRITICAL_ILLNESS,
    status: "quoted",
    notes: "",
    benefitPlans: [
      {
        identifier: "critical-illness-plan",
        name: "Critical Illness Plan",
        numberOfLives: 0,
        type: BenefitPlanTypeEnum.NOT_APPLICABLE,
        policyPeriod: BaseBenefitPlanPolicyPeriod.current,
        fundingType: FundingTypeEnum.FULLY_INSURED,
        optionName: PlanOptionNameEnum.NOT_APPLICABLE,
        benefitClassAvailability: [],
        rateSchedule: [],
        benefits: {
          objectType: "criticalIllness",

          // ========================================
          // EMPLOYEE BENEFITS (Grid Format - Flat Fields)
          // ========================================

          // Employee - Benefit Description
          employeeBenefitDescriptionType: undefined,
          employeeBenefitDescriptionAmount: undefined,
          employeeBenefitDescriptionMinimum: undefined,

          // Employee - Benefit Maximum
          employeeBenefitMaximumType: undefined,
          employeeBenefitMaximumDollarAmount: undefined,
          employeeBenefitMaximumPercent: undefined,

          // Employee - Guarantee Issue
          employeeGuaranteeIssueType: undefined,
          employeeGuaranteeIssueAmount: undefined,

          // ========================================
          // DEPENDENTS (Grid Format - Flat Fields)
          // ========================================

          // Dependent Benefits Included (FE only field)
          dependentBenefitsIncluded: undefined,

          // Spouse - Benefit Description
          spouseBenefitDescriptionType: undefined,
          spouseBenefitDescriptionAmount: undefined,
          spouseBenefitDescriptionMinimum: undefined,

          // Spouse - Benefit Maximum
          spouseBenefitMaximumType: undefined,
          spouseBenefitMaximumDollarAmount: undefined,
          spouseBenefitMaximumPercent: undefined,

          // Spouse - Guarantee Issue
          spouseGuaranteeIssueType: undefined,
          spouseGuaranteeIssueAmount: undefined,

          // Child - Benefit Description
          childBenefitDescriptionType: undefined,
          childBenefitDescriptionAmount: undefined,
          childBenefitDescriptionMinimum: undefined,

          // ========================================
          // ILLNESS-SPECIFIC BENEFITS (Grid Format - Flat Fields)
          // ========================================

          // Invasive Cancer
          invasiveCancerType: undefined,
          invasiveCancerAmount: undefined,

          // Non-Invasive Cancer
          nonInvasiveCancerType: undefined,
          nonInvasiveCancerAmount: undefined,

          // Category 1 - Heart Attack
          category1HeartAttackType: undefined,
          category1HeartAttackAmount: undefined,

          // Category 1 - Stroke
          category1StrokeType: undefined,
          category1StrokeAmount: undefined,

          // Kidney Failure
          kidneyFailureType: undefined,
          kidneyFailureAmount: undefined,

          // ========================================
          // PAYOUT AND OCCURRENCE LIMITS (Grid Format - Flat Fields)
          // ========================================

          // Maximum Payout
          maximumPayoutType: undefined,
          maximumPayoutAmount: undefined,

          // Occurrence of Different Illness
          occurrenceOfDifferentIllnessType: undefined,
          occurrenceOfDifferentIllnessMonths: undefined,

          // Additional Occurrence of Same Illness
          additionalOccurrenceOfSameIllnessType: undefined,
          additionalOccurrenceOfSameIllnessMonths: undefined,

          // ========================================
          // AGE AND CONDITIONS (Grid Format - Flat Fields)
          // ========================================

          // Age Reduction Schedule
          ageReductionScheduleType: undefined,
          ageReductionScheduleSchedules: [],

          // Pre-Existing Condition
          preExistingConditionType: undefined,
          preExistingConditionMonthsDetail: undefined,

          // ========================================
          // ADDITIONAL BENEFITS AND OPTIONS (Grid Format - Flat Fields)
          // ========================================

          // Portability
          portability: undefined,

          // Wellness Benefit
          wellnessBenefitType: undefined,
          wellnessBenefitAmount: undefined,

          // Annual Open Enrollment
          annualOpenEnrollment: undefined,

          // Age Basis
          ageBasis: undefined,

          // Benefit Class Availability
          benefitClassAvailability: undefined,

          // Extended (non-v5) fields — CD-390/CD-2074
          skinCancer: undefined,
          advancedParkinsons: undefined,
          als: undefined,
          eligibilityHours: undefined,
        },
      } as RfpBenefitPlan,
    ],
  };
}
